import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ImageField } from "../../components/admin/fields";
import { toast } from "sonner";

const EMPTY = {
  name: "", type: "percentage", discount_value: 0, max_discount: 0, buy_qty: 1, get_qty: 1,
  scope: "all", product_ids: [], category_ids: [], min_cart_value: 0, min_qty: 0,
  coupon_code: "", usage_limit: 0, per_customer_limit: 0, first_order_only: false,
  start_at: "", end_at: "", active: true,
  title: "", description: "", banner_image: "", popup_image: "",
  cta_text: "", cta_link: "", show_banner: false, show_popup: false,
};

const TYPE_LABELS = { percentage: "% Discount", flat: "Flat ₹ Off", bxgy: "Buy X Get Y Free", free_shipping: "Free Shipping" };

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, discount_value: parseFloat(form.discount_value) || 0, max_discount: parseFloat(form.max_discount) || 0, buy_qty: parseInt(form.buy_qty) || 1, get_qty: parseInt(form.get_qty) || 1, min_cart_value: parseFloat(form.min_cart_value) || 0, min_qty: parseInt(form.min_qty) || 0, usage_limit: parseInt(form.usage_limit) || 0, per_customer_limit: parseInt(form.per_customer_limit) || 0 };
    try {
      if (editing) await api.put(`/admin/campaigns/${editing.id}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success("Campaign saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await api.delete(`/admin/campaigns/${c.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const toggleActive = async (c, v) => {
    await api.put(`/admin/campaigns/${c.id}`, { ...EMPTY, ...c, active: v }).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  if (!campaigns) return <div className="h-40 bg-muted animate-pulse" />;

  const now = new Date().toISOString();
  const liveStatus = (c) => {
    if (!c.active) return ["Inactive", "text-muted-foreground"];
    if (c.start_at && c.start_at > now) return ["Scheduled", "text-blue-700"];
    if (c.end_at && c.end_at < now) return ["Expired", "text-destructive"];
    return ["Live", "text-green-700"];
  };

  return (
    <div data-testid="admin-campaigns">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-semibold">Campaigns & Offers <span className="text-sm text-muted-foreground font-sans font-normal">({campaigns.length})</span></h1>
        <button onClick={openNew} className="bg-[#111] text-[#FAF9F6] px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 flex items-center gap-2" data-testid="add-campaign-button">
          <Plus strokeWidth={1.5} size={14} /> Create Campaign
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-6">Campaigns auto-activate and expire by date. The single best applicable campaign applies per order — totals are always calculated on the server, so discounts can never be faked.</p>

      {campaigns.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-white border border-black/10 p-12 text-center" data-testid="no-campaigns">No campaigns yet. Create your first offer — Diwali sale, coupon code, Buy 1 Get 1…</p>
      ) : (
        <div className="flex flex-col gap-3">
          {campaigns.map((c) => {
            const [status, cls] = liveStatus(c);
            return (
              <div key={c.id} className="bg-white border border-black/10 p-4 flex flex-wrap items-center gap-3" data-testid={`campaign-${c.id}`}>
                <Switch checked={c.active} onCheckedChange={(v) => toggleActive(c, v)} data-testid={`campaign-toggle-${c.id}`} />
                <div className="flex-1 min-w-[180px]">
                  <p className="font-medium text-sm">{c.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {TYPE_LABELS[c.type]}
                    {c.type === "percentage" && ` · ${c.discount_value}%`}
                    {c.type === "flat" && ` · ₹${c.discount_value}`}
                    {c.type === "bxgy" && ` · Buy ${c.buy_qty} Get ${c.get_qty}`}
                    {c.scope !== "all" && ` · ${c.scope === "products" ? c.product_ids.length + " products" : c.category_ids.length + " categories"}`}
                    {c.coupon_code && ` · Code: ${c.coupon_code}`}
                    {c.min_cart_value > 0 && ` · Min ₹${c.min_cart_value}`}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {c.start_at ? new Date(c.start_at).toLocaleString("en-IN") : "Anytime"} → {c.end_at ? new Date(c.end_at).toLocaleString("en-IN") : "No end"} · Used {c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""} times
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-widest ${cls}`} data-testid={`campaign-status-${c.id}`}>{status}</span>
                <button onClick={() => openEdit(c)} className="p-2 hover:text-gold transition-colors duration-300" data-testid={`campaign-edit-${c.id}`} aria-label="Edit"><Pencil strokeWidth={1.5} size={16} /></button>
                <button onClick={() => remove(c)} className="p-2 hover:text-destructive transition-colors duration-300" data-testid={`campaign-delete-${c.id}`} aria-label="Delete"><Trash2 strokeWidth={1.5} size={16} /></button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF9F6] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Campaign" : "Create Campaign"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Campaign Name *</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 bg-white" placeholder="e.g. Diwali Sale 2026" data-testid="camp-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Discount Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-2 rounded-sm bg-white" data-testid="camp-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage" data-testid="type-percentage">Percentage Discount</SelectItem>
                  <SelectItem value="flat" data-testid="type-flat">Flat ₹ Discount</SelectItem>
                  <SelectItem value="bxgy" data-testid="type-bxgy">Buy X Get Y Free</SelectItem>
                  <SelectItem value="free_shipping" data-testid="type-free-shipping">Free Shipping</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type === "bxgy" ? (
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-widest">Buy Qty</Label>
                  <Input type="number" min="1" value={form.buy_qty} onChange={(e) => set("buy_qty", e.target.value)} className="mt-2 bg-white" data-testid="camp-buy-qty" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-widest">Get Free Qty</Label>
                  <Input type="number" min="1" value={form.get_qty} onChange={(e) => set("get_qty", e.target.value)} className="mt-2 bg-white" data-testid="camp-get-qty" />
                </div>
              </div>
            ) : form.type !== "free_shipping" ? (
              <div>
                <Label className="text-xs uppercase tracking-widest">{form.type === "percentage" ? "Discount %" : "Discount ₹"}</Label>
                <Input type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => set("discount_value", e.target.value)} className="mt-2 bg-white" data-testid="camp-value" />
              </div>
            ) : <div />}
            {form.type === "percentage" && (
              <div>
                <Label className="text-xs uppercase tracking-widest">Max Discount ₹ (0 = no cap)</Label>
                <Input type="number" min="0" value={form.max_discount} onChange={(e) => set("max_discount", e.target.value)} className="mt-2 bg-white" data-testid="camp-max-discount" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Applies To</Label>
              <Select value={form.scope} onValueChange={(v) => set("scope", v)}>
                <SelectTrigger className="mt-2 rounded-sm bg-white" data-testid="camp-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="scope-all">Entire Store</SelectItem>
                  <SelectItem value="products" data-testid="scope-products">Specific Products</SelectItem>
                  <SelectItem value="categories" data-testid="scope-categories">Specific Categories</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Coupon Code (blank = auto-apply)</Label>
              <Input value={form.coupon_code} onChange={(e) => set("coupon_code", e.target.value.toUpperCase())} className="mt-2 bg-white" placeholder="e.g. DIWALI10" data-testid="camp-coupon" />
            </div>
            {form.scope === "products" && (
              <div className="md:col-span-2 border border-black/10 p-3 max-h-40 overflow-y-auto bg-white">
                <Label className="text-xs uppercase tracking-widest">Select Products</Label>
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm py-1" data-testid={`camp-product-${p.id}`}>
                    <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={(e) => set("product_ids", e.target.checked ? [...form.product_ids, p.id] : form.product_ids.filter((x) => x !== p.id))} />
                    {p.name}
                  </label>
                ))}
              </div>
            )}
            {form.scope === "categories" && (
              <div className="md:col-span-2 border border-black/10 p-3 max-h-40 overflow-y-auto bg-white">
                <Label className="text-xs uppercase tracking-widest">Select Categories</Label>
                {categories.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm py-1" data-testid={`camp-category-${c.id}`}>
                    <input type="checkbox" checked={form.category_ids.includes(c.id)} onChange={(e) => set("category_ids", e.target.checked ? [...form.category_ids, c.id] : form.category_ids.filter((x) => x !== c.id))} />
                    {c.name}
                  </label>
                ))}
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Min Cart Value ₹ (0 = none)</Label>
              <Input type="number" min="0" value={form.min_cart_value} onChange={(e) => set("min_cart_value", e.target.value)} className="mt-2 bg-white" data-testid="camp-min-cart" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Min Quantity (0 = none)</Label>
              <Input type="number" min="0" value={form.min_qty} onChange={(e) => set("min_qty", e.target.value)} className="mt-2 bg-white" data-testid="camp-min-qty" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Total Usage Limit (0 = unlimited)</Label>
              <Input type="number" min="0" value={form.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} className="mt-2 bg-white" data-testid="camp-usage-limit" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Per-Customer Limit (0 = unlimited)</Label>
              <Input type="number" min="0" value={form.per_customer_limit} onChange={(e) => set("per_customer_limit", e.target.value)} className="mt-2 bg-white" data-testid="camp-customer-limit" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Start Date & Time (blank = now)</Label>
              <Input type="datetime-local" value={form.start_at ? form.start_at.slice(0, 16) : ""} onChange={(e) => set("start_at", e.target.value ? new Date(e.target.value).toISOString() : "")} className="mt-2 bg-white" data-testid="camp-start" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">End Date & Time (blank = never)</Label>
              <Input type="datetime-local" value={form.end_at ? form.end_at.slice(0, 16) : ""} onChange={(e) => set("end_at", e.target.value ? new Date(e.target.value).toISOString() : "")} className="mt-2 bg-white" data-testid="camp-end" />
            </div>
            <label className="flex items-center gap-3 text-sm" data-testid="camp-first-order-row">
              <Switch checked={form.first_order_only} onCheckedChange={(v) => set("first_order_only", v)} data-testid="camp-first-order" /> First-order customers only
            </label>
            <label className="flex items-center gap-3 text-sm" data-testid="camp-active-row">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="camp-active" /> Active
            </label>

            <div className="md:col-span-2 border-t border-black/10 pt-5 mt-1">
              <p className="small-caps text-gold mb-4">Promotion Display (optional)</p>
              <p className="text-[11px] text-muted-foreground mb-4">Show this campaign as a homepage banner and/or a popup to visitors. Nothing appears unless you enable it here — and it hides automatically when the campaign expires.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Public Title</Label>
                  <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-2 bg-white" placeholder="e.g. Festive Offer — 15% Off" data-testid="camp-title" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Button Text</Label>
                  <Input value={form.cta_text} onChange={(e) => set("cta_text", e.target.value)} className="mt-2 bg-white" placeholder="Shop the Offer" data-testid="camp-cta-text" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest">Short Description</Label>
                  <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-2 bg-white" placeholder="On selected handcrafted pieces" data-testid="camp-description" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Button Link</Label>
                  <Input value={form.cta_link} onChange={(e) => set("cta_link", e.target.value)} className="mt-2 bg-white" placeholder="/shop" data-testid="camp-cta-link" />
                </div>
                <div className="flex flex-col gap-4">
                  <label className="flex items-center gap-3 text-sm" data-testid="camp-show-banner-row">
                    <Switch checked={form.show_banner} onCheckedChange={(v) => set("show_banner", v)} data-testid="camp-show-banner" /> Show homepage banner
                  </label>
                  <label className="flex items-center gap-3 text-sm" data-testid="camp-show-popup-row">
                    <Switch checked={form.show_popup} onCheckedChange={(v) => set("show_popup", v)} data-testid="camp-show-popup" /> Show popup to visitors
                  </label>
                </div>
                <ImageField label="Banner Image (wide)" value={form.banner_image} onChange={(v) => set("banner_image", v)} testid="camp-banner-image" />
                <ImageField label="Popup Image" value={form.popup_image} onChange={(v) => set("popup_image", v)} testid="camp-popup-image" />
              </div>
            </div>
            <div className="md:col-span-2">
              <button type="submit" className="w-full bg-[#111] text-[#FAF9F6] py-3.5 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300" data-testid="camp-save">
                Save Campaign
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
